import React from 'react';
import TenantRow, { TenantDisplayProps, TenantRowLayout, compliancePercent } from './TenantRow';
import { ResellerGroup } from './tenantGroups';

interface ResellerGroupBoxProps extends TenantDisplayProps {
  group: ResellerGroup;
  expanded: boolean;
  onToggle: () => void;
}

const sum = (values: number[]) => values.reduce((total, n) => total + (n || 0), 0);

// A reseller and the customer tenants it manages: red-bordered box, collapsed to the reseller's row until opened.
const ResellerGroupBox = ({ group, expanded, onToggle, ...display }: ResellerGroupBoxProps) => {
  const { reseller, tenants } = group;
  const bodyId = `reseller-tenants-${reseller.tenantId}`;

  const devices = sum(tenants.map((t) => t.totalAgentCount));
  const compliant = sum(tenants.map((t) => t.compliantAgentCount));
  const licenses = sum(tenants.map((t) => t.totalLicenses));

  return (
    <section
      aria-label={`Reseller ${display.maskName(reseller.tenantName, reseller.tenantId)}`}
      className="rounded-xl border border-red-700 bg-zinc-900/60"
    >
      <div className="p-5">
        <TenantRowLayout
          tenant={reseller}
          tag="Reseller"
          summary={tenants.length === 0 ? 'No tenants' : `${tenants.length} ${tenants.length === 1 ? 'tenant' : 'tenants'}`}
          stats={[
            { label: 'Compliance', value: compliancePercent(compliant, devices) },
            { label: 'Devices', value: devices.toLocaleString() },
            { label: 'Licenses', value: licenses.toLocaleString() },
          ]}
          actionLabel="View Reseller"
          onAction={() => display.onView(reseller)}
          toggle={{ expanded, onToggle, controls: bodyId }}
          {...display}
        />
      </div>

      {expanded && (
        <div id={bodyId} className="border-t border-zinc-800 p-5">
          {tenants.length === 0 ? (
            <div className="rounded-lg border border-dashed border-zinc-700 py-8 text-center text-sm text-gray-400">
              No tenants under this reseller yet.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {tenants.map((tenant) => (
                <TenantRow key={tenant.tenantId} tenant={tenant} {...display} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default ResellerGroupBox;
